import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, Info } from 'lucide-react';
import { Button, Dialog } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { subdivisionApi } from '../../subdivision/api/subdivision.api.js';
import type { ScopeOption } from '../../rbac/ui/ScopePicker.js';
import { PositionField, EMPTY_POSITION, type PositionValue } from './PositionField.js';
import { staffApi, type StaffDto, type WorkplaceBody } from '../api/staff.api.js';

/**
 * ISH JOYI — korxona, lavozim va BO'LINMA.
 *
 * Oyna ikki holatni bir shaklda eplaydi va farqni foydalanuvchi sezmaydi:
 *
 *   – ish joyi BOR bo'lsa, korxona qulflangan holda ko'rsatiladi va faqat
 *     lavozim bilan bo'linma o'zgaradi (`PATCH /employments/:id`);
 *   – ish joyi YO'Q bo'lsa, uchalasi ham tanlanadi (`POST .../employments`).
 *
 * KORXONA NEGA QULFLANGAN? U kadrlar bazasidan keladi va yagona manba
 * bo'lib qolishi kerak: bu yerda o'zgartirilsa, xodim tizimda bir korxonada,
 * kadrlarda esa boshqasida ko'rinib qolardi va keyin qaysi biri to'g'ri
 * ekanini hech kim ayta olmasdi. Korxona haqiqatan ham o'zgargan bo'lsa,
 * uni kadrlar bo'limi o'z bazasida o'zgartiradi.
 *
 * BO'LINMA esa aynan shu yerda beriladi: u kadrlar bazasida umuman yo'q
 * («R va AX», «TB» — korxonaning ichki bo'limlari) va uni faqat shu tizim
 * biladi. «Mening omborim» bo'limi ham shunga tayanadi.
 */

export interface WorkplaceDialogProps {
  /** `null` — oyna yopiq. */
  staff: StaffDto | null;
  /** Korxonalar ro'yxati — ilova qatlamidan keladi (`useScopeOptions`). */
  scopeOptions: ScopeOption[];
  onClose: () => void;
  /** Saqlangach ro'yxatni yangilash uchun. */
  onSaved: () => void;
}

/**
 * Ish joyi bo'la oladigan obyektlar — OMBORDAN boshqa hammasi.
 *
 * Ilgari bu yerda `kind === 'ORG_UNIT'` turardi va u MTU ning O'ZINI ham,
 * stansiyalarni ham tashlab yuborardi. Oqibati og'ir edi: MTU apparatida
 * ishlaydigan xodimni hech qayerga biriktirib bo'lmasdi va MTU ga
 * biriktirilgan bo'limlar («Kotibiyat», «Moliya bo'limi» — Qarshi MTU da
 * o'nlab bor) umuman ochilmasdi. Ekranda bu «bo'lim yo'q» bo'lib
 * ko'rinardi.
 *
 * `employment.org_unit_id` istalgan korxonaga ishora qiladi — ombor esa
 * ma'muriy birlik emas va ish joyi bo'la olmaydi.
 */
export const workplaceUnitsOf = (options: readonly ScopeOption[]): ScopeOption[] =>
  options.filter((option) => option.kind !== 'WAREHOUSE');

const errorText = (error: unknown, fallback: string): string =>
  error instanceof ApiClientError ? error.message : fallback;

export const WorkplaceDialog = ({
  staff,
  scopeOptions,
  onClose,
  onSaved,
}: WorkplaceDialogProps) => {
  const [orgUnitId, setOrgUnitId] = useState('');
  const [position, setPosition] = useState<PositionValue>(EMPTY_POSITION);
  const [subdivisionId, setSubdivisionId] = useState('');

  const hasEmployment = staff !== null && staff.employmentId !== null;

  /* Ish joyi bor bo'lsa korxona o'zgarmaydi — tanlagich ham chizilmaydi. */
  const effectiveOrgUnitId = hasEmployment ? (staff.orgUnitId ?? '') : orgUnitId;

  // Har ochilishda maydonlar XODIMNING o'z ma'lumoti bilan to'ladi: bo'sh
  // ochilsa, administrator allaqachon to'g'ri turgan lavozimni qaytadan
  // qidirib topishga majbur bo'lardi.
  useEffect(() => {
    if (staff === null) return;
    setOrgUnitId(staff.orgUnitId ?? '');
    setPosition(staff.positionId === null ? EMPTY_POSITION : { id: staff.positionId, name: null });
    setSubdivisionId(staff.subdivisionId ?? '');
  }, [staff]);

  const workplaceUnits = workplaceUnitsOf(scopeOptions);

  /*
   * Bo'linmalar FAQAT tanlangan korxonaniki. Aks holda «R va AX» ro'yxatda
   * o'nlab marta takrorlanardi va qaysi biri qaysi korxonaniki ekani
   * bilinmasdi — server esa begonasini baribir rad etardi.
   */
  const subdivisions = useQuery({
    queryKey: ['subdivisions', 'for-staff', effectiveOrgUnitId],
    queryFn: () => subdivisionApi.list({ orgUnitId: effectiveOrgUnitId, activeOnly: true }),
    enabled: staff !== null && effectiveOrgUnitId !== '',
    /*
     * KESHLANMAYDI (`staleTime: 0`).
     *
     * Bo'lim ko'pincha aynan shu ishning o'rtasida ochiladi: kadrlar xodimi
     * ro'yxatni bo'sh ko'radi, «Bo'linmalar» sahifasiga o'tib yangisini
     * qo'shadi va qaytadi. Uzoq keshda o'sha yangi bo'lim bir necha daqiqa
     * ko'rinmasdi va odam uni yaratilmagan deb o'ylardi.
     */
    staleTime: 0,
  });

  const save = useMutation({
    mutationFn: () => {
      if (staff === null) throw new Error('Xodim tanlanmagan');

      /* Bo'sh tanlov `null` bo'lib ketadi — bu «bo'linmani bekor qilish».
         `undefined` yuborilsa server uni «tegilmasin» deb o'qirdi va
         biriktirilgan bo'limni olib tashlashning iloji qolmasdi. */
      const body: WorkplaceBody = {
        /* Lavozim ikki xil kelishi mumkin: ro'yxatdan `positionId` yoki
           qo'lda yozilgan `positionName`. Ikkalasi BIRGA yuborilmaydi —
           server ularni rad etadi (`employment.ts` dagi izoh). */
        ...(position.name === null
          ? { positionId: position.id }
          : { positionId: null, positionName: position.name }),
        subdivisionId: subdivisionId === '' ? null : subdivisionId,
      };

      return staff.employmentId === null
        ? staffApi.createEmployment(staff.id, { ...body, orgUnitId })
        : staffApi.updateEmployment(staff.employmentId, body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  /*
   * Faqat KORXONA majburiy — u ish joyining o'zi degani.
   *
   * Lavozim ataylab shart emas: ma'lumotnomada aynan mos lavozim ko'pincha
   * topilmaydi va uni majburiy qilish kadrlar xodimini taxminan birini
   * tanlashga majburlardi. Taxmin bo'sh maydondan yomonroq — u ekranga ham,
   * hujjatga ham haqiqatdek chiqadi.
   */
  const blocked = !hasEmployment && orgUnitId === '';

  const orgUnitLabel =
    staff?.orgUnitName ??
    workplaceUnits.find((unit) => unit.id === orgUnitId)?.label ??
    null;

  return (
    <Dialog
      open={staff !== null}
      onClose={onClose}
      title={t('staff.workplaceTitle', { name: staff?.fullName ?? '' })}
      description={t('staff.workplaceHint')}
      busy={save.isPending}
      onSubmit={() => {
        if (!blocked) save.mutate();
      }}
      {...(save.error === null || save.error === undefined
        ? {}
        : { error: errorText(save.error, t('staff.workplaceFailed')) })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            {t('staff.workplaceCancel')}
          </Button>
          <Button loading={save.isPending} disabled={blocked} onClick={() => save.mutate()}>
            {t('staff.workplaceSave')}
          </Button>
        </>
      }
    >
      {staff === null ? null : (
        <div className="flex flex-col gap-4">
          {hasEmployment ? (
            /* Qulflangan korxona MAYDON emas, ma'lumot bo'lib ko'rsatiladi:
               o'chirilgan tanlagich «nega ishlamayapti?» degan savol
               tug'diradi, javobini esa bermaydi. */
            <div className="border-border bg-surface-2 flex items-start gap-2.5 rounded-[var(--radius-sm)] border px-3 py-2.5">
              <Building2 size={16} aria-hidden className="text-text-muted mt-0.5 shrink-0" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-text-muted text-xs">{t('staff.workplaceOrgUnit')}</span>
                <span className="text-text text-sm font-medium">
                  {orgUnitLabel ?? t('staff.workplaceOrgUnitUnknown')}
                </span>
                <span className="text-text-faint text-xs text-pretty">
                  {t('staff.workplaceOrgUnitLocked')}
                </span>
              </div>
            </div>
          ) : (
            <Select
              label={t('staff.workplaceOrgUnit')}
              placeholder={t('staff.workplaceOrgUnitPlaceholder')}
              value={orgUnitId}
              options={workplaceUnits.map((unit) => ({
                value: unit.id,
                label:
                  unit.hint === null || unit.hint === undefined
                    ? unit.label
                    : `${unit.label} · ${unit.hint}`,
              }))}
              onChange={(event) => {
                setOrgUnitId(event.target.value);
                // Korxona almashsa bo'linma BEKOR bo'ladi: eski bo'lim yangi
                // korxonaga tegishli emas va server uni rad etadi.
                setSubdivisionId('');
              }}
            />
          )}

          <PositionField
            label={t('staff.workplacePosition')}
            placeholder={t('staff.workplacePositionNone')}
            hint={t('staff.workplacePositionHint')}
            value={position}
            onChange={setPosition}
          />

          <Select
            label={t('staff.workplaceSubdivision')}
            placeholder={t('staff.workplaceSubdivisionNone')}
            hint={t('staff.workplaceSubdivisionHint')}
            value={subdivisionId}
            disabled={effectiveOrgUnitId === '' || subdivisions.isPending}
            options={(subdivisions.data?.items ?? []).map((row) => ({
              value: row.id,
              label: row.name,
            }))}
            onChange={(event) => setSubdivisionId(event.target.value)}
          />

          {/*
            BO'SH TANLAGICHNING SABABI HAR DOIM AYTILADI.
            Uch xil holat bir xil ko'rinardi — bo'sh ro'yxat: bo'lim
            ochilmagan, so'rov yiqilgan, yoki ruxsat yetmagan. Foydalanuvchi
            uchun bu «tizim buzuq» degani va u sababni topa olmasdi.
          */}
          {effectiveOrgUnitId === '' ? null : subdivisions.isError ? (
            <p
              role="alert"
              className="border-danger/30 bg-danger-soft text-danger flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
            >
              <Info size={14} aria-hidden className="mt-px shrink-0" />
              <span className="text-pretty">
                {errorText(subdivisions.error, t('staff.workplaceSubdivisionsFailed'))}
              </span>
            </p>
          ) : subdivisions.isSuccess && subdivisions.data.items.length === 0 ? (
            <p className="text-text-muted flex items-start gap-2 text-xs">
              <Info size={14} aria-hidden className="mt-px shrink-0" />
              <span className="text-pretty">{t('staff.workplaceNoSubdivisions')}</span>
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};
